import { Loader } from 'components/ui';
import { useBuyerOrg } from 'hooks/use-buyer-org';
import { NextPage } from 'next';
import { signIn, useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { PATHS, REDIRECT_URL, SAFE_PATHS_NO_ORG } from 'settings/config';
import { logger } from 'lib/hatchd-utils';
import { useStore } from '../../lib/store';

type Options = {
  hideHeader?: boolean;
  hideNav?: boolean;
  overrideHeaderContent?: boolean;
  hideHomeLink?: boolean;
  marketplace?: boolean;
  app?: boolean;
};

/** Protect this page and prevent rendering if the current user session is invalid */
export function publicRoute<T extends {} = {}>(
  WrappedComponent: NextPage<T>,
  options?: Options
) {
  // Try to create a nice displayName for React Dev Tools.
  const displayName =
    WrappedComponent.displayName || WrappedComponent.name || 'Component';

  // Creating the inner component. The calculated Props type here is the where the magic happens.
  const ComponentWithAuth = (props: T) => {
    const { data: session, status } = useSession();
    const loading = status === 'loading';
    const router = useRouter();
    const { data: buyerOrg } = useBuyerOrg();
    const [loadData, setLoadData] = useState(false);
    const activeBuyerOrgId = useStore((store) => store.activeBuyerOrgId);

    // Redirect to login page, user is not authenticated
    useEffect(() => {
      if (!session && !loading) {
        setLoadData(true);
      }
    }, [loading, session]);

    useEffect(() => {
      if (
        !activeBuyerOrgId &&
        !SAFE_PATHS_NO_ORG.some((route) => {
          return route.includes(router.pathname);
        }) &&
        session?.accessToken
      ) {
        logger.info('No buyer org set, redirecting to org picker page');
        localStorage.setItem(REDIRECT_URL, router.asPath);
        router.push(PATHS.organisation.picker);
      }
    }, [activeBuyerOrgId, router.pathname, session?.accessToken]);

    // // Render the passed in component
    if (
      (session &&
        (buyerOrg ||
          SAFE_PATHS_NO_ORG.some((route) => {
            return route.includes(router.pathname);
          }))) ||
      (!session && (!buyerOrg || SAFE_PATHS_NO_ORG.includes(router.pathname)))
    ) {
      return <WrappedComponent {...props} />;
    }

    if (loadData) {
      signIn();
    }

    return (
      <div style={{ minHeight: '100vh' }}>
        <Loader floating size={5} />
      </div>
    );
  };

  ComponentWithAuth.displayName = `withAuth(${displayName})`;
  ComponentWithAuth.overrideHeaderContent = options?.overrideHeaderContent;
  ComponentWithAuth.hideNav = !!options?.hideNav;
  ComponentWithAuth.hideHeader = !!options?.hideHeader;
  ComponentWithAuth.hideHomeLink = !!options?.hideHomeLink;
  ComponentWithAuth.marketplace = !!options?.marketplace;
  ComponentWithAuth.app = !!options?.app;

  return ComponentWithAuth;
}
